import React, { useEffect, useMemo, useState } from 'react'
import type { EntryType } from '../types'
import { useTracker } from '../context/TrackerContext'
import { buildCategoryCounts, getTimeBucket } from '../utils/timeOfDay'

const AMOUNT_OPTIONS = [1, 2, 3, 4, 5, 10]
const ENTRY_TYPES: EntryType[] = ['did', 'ate', 'drank', 'other']

type LoggerFilter = EntryType | 'all'

export default function Logger({ onDone, onRefresh }: { onDone: () => void; onRefresh: () => void }) {
  const { settings, addLog, logs } = useTracker()
  const [selectedType, setSelectedType] = useState<LoggerFilter>('all')
  const [customType, setCustomType] = useState<EntryType>('did')
  const [customCategory, setCustomCategory] = useState('')
  const [notes, setNotes] = useState('')
  const [quantityInput, setQuantityInput] = useState('1')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const [actionFilter, setActionFilter] = useState('')
  const trimmedCustom = customCategory.trim()
  const parsedQuantity = Number.parseInt(quantityInput, 10)
  const normalizedQuantity = Number.isFinite(parsedQuantity) ? Math.max(1, parsedQuantity) : 1

  const categoriesByType = useMemo(
    () => ({
      did: settings.categories.did || [],
      ate: settings.categories.ate || [],
      drank: settings.categories.drank || [],
      other: settings.categories.other || [],
    }),
    [settings]
  )
  const activeCustomType = selectedType === 'all' ? customType : selectedType
  const customExists = trimmedCustom
    ? categoriesByType[activeCustomType].some(cat => cat.trim().toLowerCase() === trimmedCustom.toLowerCase())
    : false

  const noCategories = ENTRY_TYPES.every(type => categoriesByType[type].length === 0)
  const timeOfDay = useMemo(() => getTimeBucket(new Date()), [])
  const twoWeeksAgo = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() - 14)
    return date
  }, [])
  const categoryCounts = useMemo(() => buildCategoryCounts(logs, twoWeeksAgo), [logs, twoWeeksAgo])
  const categoryStats = useMemo(() => {
    const stats: Record<EntryType, Record<string, { totalCount: number; lastLogMs: number }>> = {
      did: {},
      ate: {},
      drank: {},
      other: {},
    }
    for (const log of logs) {
      const perType = stats[log.type]
      if (!perType[log.category]) {
        perType[log.category] = { totalCount: 0, lastLogMs: 0 }
      }
      perType[log.category].totalCount += 1
      const timestamp = Date.parse(log.date)
      if (Number.isFinite(timestamp)) {
        perType[log.category].lastLogMs = Math.max(perType[log.category].lastLogMs, timestamp)
      }
    }
    return stats
  }, [logs])
  const actionItems = useMemo(() => {
    const items = ENTRY_TYPES.flatMap(type => {
      const countsForType = categoryCounts[type] ?? {}
      const statsForType = categoryStats[type] ?? {}
      return categoriesByType[type].map(category => ({
        type,
        category,
        count: countsForType[category]?.[timeOfDay] ?? 0,
        lastLogMs: statsForType[category]?.lastLogMs ?? 0,
        totalCount: statsForType[category]?.totalCount ?? 0,
      }))
    })
    const filteredByType = selectedType === 'all' ? items : items.filter(item => item.type === selectedType)
    const trimmedFilter = actionFilter.trim().toLowerCase()
    const filtered = trimmedFilter
      ? filteredByType.filter(item => item.category.toLowerCase().includes(trimmedFilter))
      : filteredByType
    return filtered.sort((a, b) => {
      if (a.count !== b.count) return b.count - a.count
      if (a.lastLogMs !== b.lastLogMs) return b.lastLogMs - a.lastLogMs
      if (a.totalCount !== b.totalCount) return b.totalCount - a.totalCount
      return a.category.localeCompare(b.category)
    })
  }, [actionFilter, categoriesByType, categoryCounts, categoryStats, selectedType, timeOfDay])

  useEffect(() => {
    if (selectedType !== 'all') {
      setCustomType(selectedType)
    }
  }, [selectedType])

  async function logEntry(category: string, type: EntryType) {
    const trimmed = category.trim()
    if (!trimmed || saving) return
    setSaving(true)
    try {
      await addLog({
        type,
        category: trimmed,
        notes: notes.trim() ? notes.trim() : undefined,
        quantity: normalizedQuantity,
      })
      setNotes('')
      setCustomCategory('')
      setQuantityInput('1')
      onDone()
    } catch (err) {
      setError((err as Error).message || 'Unable to save log')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h3>Log actions</h3>
      <p className="muted">Add optional notes, pick an amount, then tap an action to log it instantly.</p>
      {error && <div className="status error" style={{ marginTop: 8 }}>{error}</div>}
      {noCategories && (
        <div className="status info" style={{ marginTop: 8 }}>
          No categories yet. Visit Settings to add at least one category for this section.
        </div>
      )}

      <div className="amount-select">
        <label>
          Amount
          <div className="amount-stepper">
            <button
              type="button"
              className="secondary small"
              onClick={() => setQuantityInput(String(Math.max(1, normalizedQuantity - 1)))}
            >
              −
            </button>
            <input
              inputMode="numeric"
              value={quantityInput}
              onChange={e => {
                const nextValue = e.target.value
                if (nextValue === '' || /^\d+$/.test(nextValue)) {
                  setQuantityInput(nextValue)
                }
              }}
              onBlur={() => {
                if (!quantityInput.trim()) {
                  setQuantityInput('1')
                } else {
                  setQuantityInput(String(normalizedQuantity))
                }
              }}
              placeholder="1"
            />
            <button
              type="button"
              className="secondary small"
              onClick={() => setQuantityInput(String(normalizedQuantity + 1))}
            >
              +
            </button>
          </div>
          <div className="amount-options">
            {AMOUNT_OPTIONS.map(opt => (
              <button
                key={opt}
                type="button"
                className={`small ${normalizedQuantity === opt ? 'button' : 'secondary'}`}
                onClick={() => setQuantityInput(String(opt))}
              >
                {opt}
              </button>
            ))}
          </div>
        </label>
      </div>

      <div className="shortcut-filter">
        <label>
          Filter shortcuts
          <input
            value={actionFilter}
            onChange={e => setActionFilter(e.target.value)}
            placeholder="Type to filter actions"
          />
        </label>
      </div>

      <div className="tracker-filters">
        <button
          className={`small ${selectedType === 'all' ? 'button' : 'secondary'}`}
          type="button"
          onClick={() => setSelectedType('all')}
        >
          All
        </button>
        {ENTRY_TYPES.map(type => (
          <button
            key={type}
            className={`small ${selectedType === type ? 'button' : 'secondary'}`}
            type="button"
            onClick={() => setSelectedType(type)}
          >
            {type === 'did' ? 'Did' : type === 'ate' ? 'Ate' : type === 'drank' ? 'Drank' : 'Other'}
          </button>
        ))}
      </div>

      <div className="category-list">
        {actionItems.map(item => (
          <button
            key={`${item.type}-${item.category}`}
            type="button"
            className="category-btn full"
            disabled={saving}
            onClick={() => logEntry(item.category, item.type)}
          >
            <span>{item.category}</span>
            {selectedType === 'all' && <small className="muted">({item.type})</small>}
            <small className="muted">· {item.count}</small>
          </button>
        ))}
      </div>

      <div style={{ marginTop: 8 }}>
        <label>
          Notes
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            style={{ width: '100%' }}
            placeholder="Add details (optional)"
          />
        </label>
      </div>
      <div className="custom-action">
        <label>
          Custom action
          <input
            value={customCategory}
            onChange={e => setCustomCategory(e.target.value)}
            placeholder="Describe a custom action"
            disabled={saving}
          />
        </label>
        {selectedType === 'all' && (
          <label>
            Group
            <select value={customType} onChange={e => setCustomType(e.target.value as EntryType)}>
              {ENTRY_TYPES.map(type => (
                <option key={type} value={type}>
                  {type === 'did' ? 'Did' : type === 'ate' ? 'Ate' : type === 'drank' ? 'Drank' : 'Other'}
                </option>
              ))}
            </select>
          </label>
        )}
        {!customExists && trimmedCustom && (
          <button
            className="button small"
            type="button"
            style={{ marginTop: 8 }}
            onClick={() => logEntry(customCategory, activeCustomType)}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save custom action'}
          </button>
        )}
        {customExists && trimmedCustom && (
          <div className="status info" style={{ marginTop: 8 }}>
            This action already exists in Settings.
          </div>
        )}
        <button className="button small secondary" type="button" style={{ marginTop: 8 }} onClick={onRefresh}>
          Refresh
        </button>
      </div>
    </div>
  )
}
